/**
 * 邀请面板：把「怎么把朋友叫进来」这件事做到一屏之内。
 *
 * ★ 顺序是刻意的：**大号房间码 → 复制房间码 → 复制链接**。
 *   DEVICES.md R3：微信会屏蔽域名，链接随时可能打不开，而 6 个字符能穿过任何渠道
 *   （微信文字、口头、截图）。所以「复制房间码」是主按钮，「复制链接」是次按钮。
 *   反过来做（链接优先）在微信被封那天会让整个邀请流程原地失效。
 *
 * ★ 复制失败不是死路：`copyText` 三级降级之后仍失败时，码本身就大号显示在上面，
 *   且挂了 `.selectable`（main.tsx 的 contextmenu 拦截对它放行），用户可以长按选中。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { selectRoomCode, useNetStore } from '../transport/applyMessage.js'
import { useUiStore } from '../store/index.js'
import { useModalDialog } from '../hooks/useModalDialog.js'
import { IconCopy, IconShare } from '../icons/index.js'
import { appOrigin, copyText, isWechat } from './env.js'
import { groupRoomCode, inviteLink } from './roomCode.js'
import './lobby.css'

export interface InviteSheetProps {
  readonly code: string
  /** 没有 roomId 就只给房间码：带不上 roomId 的链接在码被复用后会把人导进陌生房间 */
  readonly roomId?: string | null
  onClose?(): void
  /** 覆盖站点根地址（测试用） */
  readonly origin?: string
  /** 覆盖微信判定（测试用） */
  readonly wechat?: boolean
}

type CopyState = 'idle' | 'code' | 'link' | 'failed'

export function InviteSheet({ code, roomId, onClose, origin, wechat }: InviteSheetProps): ReactNode {
  const [copied, setCopied] = useState<CopyState>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inWechat = wechat ?? isWechat()
  const base = origin ?? appOrigin()
  const link = roomId == null || roomId === '' ? null : inviteLink(base, roomId, code)
  const { dialogRef, onDialogKeyDown } = useModalDialog({ open: true, onClose })

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current)
    },
    [],
  )

  const flash = useCallback((state: CopyState): void => {
    setCopied(state)
    if (timer.current !== null) clearTimeout(timer.current)
    timer.current = setTimeout(() => setCopied('idle'), 2400)
  }, [])

  const doCopy = useCallback(
    (text: string, kind: 'code' | 'link'): void => {
      void copyText(text).then((ok) => flash(ok ? kind : 'failed'))
    },
    [flash],
  )

  const panel = (
    <div
      className="sheet-scrim sheet-scrim--paper"
      role="presentation"
      onClick={() => onClose?.()}
    >
      {/* 面板本身吞掉点击：只有点遮罩才关 */}
      <div
        className="sheet-panel"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="邀请朋友"
        tabIndex={-1}
        onKeyDown={onDialogKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题和别处的按钮用同一个词（「邀请朋友」），不换同义词 */}
        <h2 className="sheet-title">邀请朋友</h2>

        <div className="code-big__row">
          <span className="code-big__cap">房间码</span>
          {/* .selectable：长按可选中，复制 API 全挂时这是最后一条退路 */}
          <strong className="code-big selectable">{groupRoomCode(code)}</strong>
        </div>

        <div className="invite-actions">
          {/* 唯一的主 CTA：码能穿过任何渠道，链接不一定（R3） */}
          <button type="button" className="lobby-cta pressable" onClick={() => doCopy(code, 'code')}>
            <IconCopy size={20} />
            复制房间码
          </button>
          <button
            type="button"
            className="lobby-btn"
            disabled={link === null}
            onClick={() => (link === null ? undefined : doCopy(link, 'link'))}
          >
            <IconShare size={18} />
            {link === null ? '链接暂不可用' : '复制链接'}
          </button>
          {/* 没动作时是空行：怎么把码发出去，上面那颗主按钮已经说完了 */}
          <p className="lobby-dock__say" aria-live="polite">
            {copied === 'code' && <span className="invite-ok">已复制</span>}
            {copied === 'link' && <span className="invite-ok">链接已复制</span>}
            {copied === 'failed' && '复制失败，长按房间码可以选中'}
          </p>
        </div>

        <button type="button" className="lobby-btn lobby-btn--ghost" onClick={() => onClose?.()}>
          关闭
        </button>
      </div>
    </div>
  )

  // 舞台开了 `contain: layout paint`，fixed 定位的遮罩会被裁进舞台里，
  // 所以挂到 body 上；没有 document 的环境（node 测试 / SSR）原地渲染，测试照样能断言内容。
  const doc = (globalThis as { document?: Document }).document
  return doc?.body == null ? panel : createPortal(panel, doc.body)
}

/**
 * 接好线的版本：房间码取自 `useNetStore`，开关挂在 `uiStore.sheet === 'roomInfo'`。
 * 牌桌顶栏放一个「邀请」按钮调 `openSheet('roomInfo')` 即可复用这一整套。
 */
export function ConnectedInviteSheet(): ReactNode {
  const code = useNetStore(selectRoomCode)
  const roomId = useNetStore((s) => s.view?.room.id ?? null)
  const sheet = useUiStore((s) => s.sheet)
  const closeSheet = useUiStore((s) => s.closeSheet)
  if (sheet !== 'roomInfo' || code === null) return null
  return <InviteSheet code={code} roomId={roomId} onClose={closeSheet} />
}

export default InviteSheet
